import { useCallback, useEffect, useMemo, useState } from "react";
import { useAuthSession } from "../auth/useAuthSession.ts";
import { getBrowserClient } from "../supabase/browser.ts";

export interface WatchlistItem {
  id: string;
  fixtureId: string;
  homeTeam: string;
  awayTeam: string;
  kickoff: string;
  league: string;
  addedAt: string;
  signal?: {
    headline: string;
    market: string;
    selection: string;
    odds: number | null;
    confidence: number | null;
    edge: number | null;
  };
}

export interface UseWatchlistItemsState {
  items: WatchlistItem[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

function confLabelToNumber(label: string | null): number | null {
  if (!label) return null;
  const map: Record<string, number> = { very_low: 1, low: 2, mid: 3, high: 4, very_high: 5 };
  return map[label.toLowerCase()] ?? null;
}

export function useWatchlistItems(): UseWatchlistItemsState {
  const supabase = useMemo(() => getBrowserClient(), []);
  const { user } = useAuthSession();
  const [items, setItems] = useState<WatchlistItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refetch = useCallback(async (): Promise<void> => {
    if (!user) {
      setItems([]);
      setError(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);

    const { data: watchRows, error: wErr } = await supabase
      .from("signal_watchlist")
      .select("*")
      .eq("user_id", user.id)
      .is("removed_at", null)
      .order("added_at", { ascending: false });

    if (wErr) {
      setItems([]);
      setError(wErr.message);
      setLoading(false);
      return;
    }

    if (!watchRows || watchRows.length === 0) {
      setItems([]);
      setLoading(false);
      return;
    }

    const fixtureIds = watchRows.map((r) => r.fixture_id);

    const [fixtureResult, signalResult] = await Promise.all([
      supabase
        .from("fixture_cache")
        .select("fixture_id, home_team, away_team, kickoff_at, league_name")
        .in("fixture_id", fixtureIds),
      supabase
        .from("signal_cards")
        .select(
          "fixture_id, summary, canonical_code, signal_label, market_odds_decimal, confidence_label, value_edge",
        )
        .in("fixture_id", fixtureIds)
        .order("generated_at", { ascending: false }),
    ]);

    const fixtureMap = new Map<
      string,
      {
        home_team: string | null;
        away_team: string | null;
        kickoff_at: string | null;
        league_name: string | null;
      }
    >();
    if (fixtureResult.data) {
      for (const f of fixtureResult.data) {
        fixtureMap.set(f.fixture_id, f);
      }
    }

    const signalMap = new Map<
      string,
      {
        summary: string | null;
        canonical_code: string;
        signal_label: string | null;
        market_odds_decimal: number | null;
        confidence_label: string | null;
        value_edge: number | null;
      }
    >();
    if (signalResult.data) {
      for (const s of signalResult.data) {
        if (!signalMap.has(s.fixture_id)) {
          signalMap.set(s.fixture_id, s);
        }
      }
    }

    const mapped: WatchlistItem[] = watchRows.map((row) => {
      const fix = fixtureMap.get(row.fixture_id);
      const sig = signalMap.get(row.fixture_id);
      return {
        id: row.id,
        fixtureId: row.fixture_id,
        homeTeam: fix?.home_team ?? "TBD",
        awayTeam: fix?.away_team ?? "TBD",
        kickoff: fix?.kickoff_at ?? "",
        league: fix?.league_name ?? "",
        addedAt: row.added_at,
        ...(sig
          ? {
              signal: {
                headline: sig.summary ?? "",
                market: sig.canonical_code,
                selection: sig.signal_label ?? "",
                odds: sig.market_odds_decimal,
                confidence: confLabelToNumber(sig.confidence_label),
                edge: sig.value_edge,
              },
            }
          : {}),
      };
    });

    setItems(mapped);
    setLoading(false);
  }, [supabase, user]);

  useEffect(() => {
    void refetch();
  }, [refetch]);

  return useMemo<UseWatchlistItemsState>(
    () => ({ items, loading, error, refetch }),
    [items, loading, error, refetch],
  );
}
